import { showConfirmation } from '../lib/confirmation';
import { formatPhone, formatDate as format, normalizeSearch, formatTime, formatName } from '../lib/format';
import SectionCard from '../components/ui/SectionCard';
import StatCard from '../components/ui/StatCard';
import PageContent from '../components/ui/PageContent';
import PageHeader from '../components/ui/PageHeader';
import StatusBadge from '../components/ui/StatusBadge';
import { Table, TableHead, TableRow, TableHeaderCell, TableCell } from '../components/ui/Table';
import SearchInput from '../components/ui/SearchInput';
import FieldLabel from '../components/ui/FieldLabel';
import Textarea from '../components/ui/Textarea';
import UiInput from '../components/ui/Input';
import Button from '../components/ui/Button';
import FilterChip from '../components/ui/FilterChip';
import { useState, useEffect, useMemo } from 'react';
import { getDropdownLabel, postWithAuth } from '../common';
import { useDispatch, useSelector } from 'react-redux';
import Header from "../components/Header";
import Sidebar from '../components/Sidebar';
import { toast } from 'react-toastify';
import useTableSort from '../components/useTableSort';
import { useNavigate } from 'react-router-dom';
import { addDays, endOfWeek, startOfWeek } from 'date-fns';
import ModalComponent from '../components/Modal';
import Section from '../components/Section';
import Input from '../components/Input';
import { setViewWorker } from '../redux/userReducer';
import { getStartOfToday } from '../constants';
import { CalendarIcon, AlertTriangleIcon, AlertCircleIcon, ListChecksIcon, Building2Icon, MapPinIcon, UsersIcon, PlusIcon, ClockIcon, CheckIcon } from '../components/NavIcons';
import LoadingPopup from '../components/LoadingPopup';
import { resolveStorageFileUrl } from '../storageUpload';
const formatLeaveDate = (date, includeYear = false) =>
    format(date, includeYear ? 'dd MMM yyyy' : 'dd MMM')

const formatLeavePeriod = (startDate, endDate) => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    const crossesYear = start.getFullYear() !== end.getFullYear()
    return `${formatLeaveDate(start, crossesYear)} to ${formatLeaveDate(end, crossesYear)}`
}

const formatAllocationCardAddress = value => {
    let address = String(value || '').trim()
    if (!address) return ''
    const statePattern = '(?:NSW|VIC|QLD|SA|WA|TAS|NT|ACT|New South Wales|Victoria|Queensland|South Australia|Western Australia|Tasmania|Northern Territory|Australian Capital Territory)'
    const removableSuffix = new RegExp(`(?:,\\s*|\\s+)(?:Australia|${statePattern}|\\d{4})\\s*$`, 'i')
    while (removableSuffix.test(address)) {
        address = address.replace(removableSuffix, '').trim()
    }
    return address.replace(/,\s*$/, '')
}

const Dashboard = () => {

    const userInfo = useSelector((state) => state.user.user)
    const activeOrg = useSelector((state) => state.user.activeOrg)
    const [loading, setLoading] = useState(false)
    const [initialLoading, setInitialLoading] = useState(true)
    const [allocationSearch, setAllocationSearch] = useState('')
    const [workerAvailabilitySearch, setWorkerAvailabilitySearch] = useState('')
    const [workerAvailabilityFilter, setWorkerAvailabilityFilter] = useState('leave')
    const [showSendMessage, setShowSendMessage] = useState(null)
    const [excludedSmsWorkerIds, setExcludedSmsWorkerIds] = useState([])
    const [smsMessage, setSmsMessage] = useState('')
    const [smsExtraNumber, setSmsExtraNumber] = useState('')
    const [smsAdditionalNumbers, setSmsAdditionalNumbers] = useState([])
    const [showViewAllocation, setShowViewAllocation] = useState(null)
    const [openingDocument, setOpeningDocument] = useState(false)

    const [clients, setClients] = useState([])
    const [sites, setSites] = useState([])
    const [workers, setWorkers] = useState([])

    const [assets, setAssets] = useState([])
    const [allocations, setAllocations] = useState([])
    const [tasks, setTasks] = useState([])
    const [timeSheets, setTimeSheets] = useState([])
    const [incompleteAllocations, setIncompleteAllocations] = useState([])
    const [workerLeaves, setWorkerLeaves] = useState([])
    const [confirmMessage, setConfirmMessage] = useState(null)
    const navigate = useNavigate()

    const dispatch = useDispatch()

    useEffect(() => {
        if (activeOrg && userInfo && activeOrg.role === 'admin') {
            const loadPageData = async () => {
                setInitialLoading(true)
                try {
                    await Promise.allSettled([
                        fetchClients(),
                        fetchSites(),
                        fetchWorkers(),
                        fetchDashboard(),
                        fetchAllocations(),
                        fetchIncompleteAllocations(),
                        fetchTasks(),
                        fetchWorkerLeaves(),
                        fetchTimeSheets(),
                    ])
                    await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)))
                } finally {
                    setInitialLoading(false)
                }
            }
            loadPageData()
        }
    }, [activeOrg, userInfo])


    const fetchClients = async () => {

        try {

            const resp = await postWithAuth('fetchClients', { listOnly: true, orgId: activeOrg.id })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setClients(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
    }

    const fetchSites = async () => {

        try {

            const resp = await postWithAuth('fetchSites', { listOnly: true, orgId: activeOrg.id })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setSites(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
    }

    const fetchWorkers = async () => {

        try {

            const resp = await postWithAuth('fetchWorkers', { listOnly: true, orgId: activeOrg.id })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setWorkers(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
    }


    const fetchDashboard = async () => {
        setLoading(true)
        try {

            const resp = await postWithAuth('fetchAssets', { orgId: activeOrg.id })

            if (resp && resp.err) {
                toast.error(resp.err)
                setLoading(false)
                return
            }

            if (resp && resp.data) {
                setAssets(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
        setLoading(false)
    }

    // fetchDashboard only returns a trimmed allocation set, which made the
    // "today" figures disagree with the Allocations screen. Read the same full
    // list that screen uses so both stay in sync.
    const fetchAllocations = async () => {
        try {
            const resp = await postWithAuth('fetchAllocations', { orgId: activeOrg.id })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setAllocations(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
    }

    const fetchIncompleteAllocations = async () => {
        try {
            const resp = await postWithAuth('fetchIncompleteAllocations', { orgId: activeOrg.id })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setIncompleteAllocations(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
    }

    const fetchTasks = async () => {
        try {
            const resp = await postWithAuth('fetchTasks', { orgId: activeOrg.id })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setTasks(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
    }

    const fetchWorkerLeaves = async () => {
        try {
            const resp = await postWithAuth('fetchWorkerLeaves', { orgId: activeOrg.id })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setWorkerLeaves(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
    }

    const fetchTimeSheets = async () => {
        try {
            // Keep the deployed endpoint until the lightweight Overview endpoint is available.
            const resp = await postWithAuth('fetchTimeSheet', { orgId: activeOrg.id, rangeStart: startOfWeek(new Date(), { weekStartsOn: 1 }).toISOString(), rangeEnd: endOfWeek(new Date(), { weekStartsOn: 1 }).toISOString() })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setTimeSheets(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
    }

    // Start of today, so the figures match the Allocations screen.
    // Keyed on the calendar day rather than [] so a dashboard left open overnight
    // rolls over at midnight, while still giving downstream memos a stable value.
    const todayKey = new Date().toDateString()
    const today = useMemo(() => getStartOfToday(), [todayKey])
    // addDays, not +24h: a daylight-saving day is 23 or 25 hours long.
    const tomorrow = useMemo(() => addDays(today, 1), [today])
    const userId = userInfo?.id
    const clientNameById = useMemo(
        () => new Map(clients.map(client => [client.id, client.name || ''])),
        [clients]
    )
    const siteById = useMemo(
        () => new Map(sites.map(site => [site.id, site])),
        [sites]
    )
    const getAllocationCardLocationLabel = allocation => {
        const hasMapMarker = !!allocation?.dropMapMarker
            || !!String(allocation?.mapMarkerAddress || allocation?.mapMarkerName || '').trim()
            || (Array.isArray(allocation?.mapMarkerCoordinates) && allocation.mapMarkerCoordinates.length >= 2)
        if (hasMapMarker) {
            const markerAddress = String(allocation?.mapMarkerAddress || '').trim()
            if (markerAddress) return markerAddress
            const markerName = String(allocation?.mapMarkerName || '').trim()
            if (markerName) return markerName
            return null
        }
        const siteAddress = siteById.get(allocation?.chosenSite)?.address
        if (String(siteAddress || '').trim()) return siteAddress
        return ''
    }
    const siteNameById = useMemo(
        () => new Map(sites.map(site => [site.id, site.name || ''])),
        [sites]
    )
    const workerById = useMemo(
        () => new Map(workers.map(worker => [worker.id, worker])),
        [workers]
    )
    const assetById = useMemo(
        () => new Map(assets.map(asset => [asset.id, asset])),
        [assets]
    )
    const isTaskForUser = (task) => {
        if (!userId) return false
        return !task.userId || task.userId === userId
    }
    const tasksWithType = useMemo(
        () => tasks.filter(isTaskForUser).map(task => ({ ...task, type: task.type || 'task' })),
        [tasks, userId]
    )
    const taskRows = useMemo(
        () => tasksWithType
            .filter(task => task.type === 'task' && !task.completed)
            .map(row => ({
                ...row,
                client: clientNameById.get(row.chosenClient) || '',
                site: siteNameById.get(row.chosenSite) || '',
            })),
        [tasksWithType, clientNameById, siteNameById]
    )
    const taskSort = useTableSort(taskRows, { key: 'dueDate', direction: 'ascending' });
    const appointmentStart = new Date(today)
    const appointmentEnd = new Date(today)
    appointmentEnd.setDate(appointmentEnd.getDate() + 1)
    appointmentEnd.setHours(23, 59, 59, 999)
    const appointmentRows = useMemo(
        () => tasksWithType
            .filter(task => task.type === 'appointment')
            .filter(task => {
                const appointmentDate = task.dueDate ? new Date(task.dueDate) : null
                if (!appointmentDate) return false
                return appointmentDate >= appointmentStart && appointmentDate <= appointmentEnd
            })
            .map(row => ({
                ...row,
                client: clientNameById.get(row.chosenClient) || '',
                site: siteNameById.get(row.chosenSite) || '',
            })),
        [tasksWithType, appointmentStart, appointmentEnd, clientNameById, siteNameById]
    )
    const appointmentSort = useTableSort(appointmentRows, { key: 'dueDate', direction: 'ascending' });
    const assetsSort = useTableSort(assets, { key: 'regoExpiry', direction: 'ascending' });
    const expiredAssets = useMemo(
        () => assets.filter(asset => [asset.regoExpiry, asset.insuranceExpiry].some(value => {
            if (!value) return false
            const expiryDate = new Date(value)
            return !Number.isNaN(expiryDate.getTime()) && expiryDate < today
        })),
        [assets, today]
    )
    const pendingWorkers = useMemo(
        () => workers.filter(worker => worker.status === 'Pending Review'),
        [workers]
    )
    const workersSort = useTableSort(pendingWorkers, { key: 'firstName', direction: 'ascending' });
    const todaysAllocations = useMemo(
        () => [...allocations, ...incompleteAllocations].filter(allocation => {
            const allocationDate = allocation?.date ? new Date(allocation.date) : null
            return allocationDate && allocationDate >= today && allocationDate < tomorrow
        }),
        [allocations, incompleteAllocations, today, tomorrow]
    )
    const scheduledTodayCount = todaysAllocations.length
    const deployedWorkersTodayCount = useMemo(
        () => todaysAllocations.reduce((count, allocation) => count + ((allocation.chosenWorkers || []).length || 0), 0),
        [todaysAllocations]
    )
    const todaysHours = useMemo(() => {
        const total = timeSheets.reduce((hours, timeSheet) => {
            if (!timeSheet?.clockIn || !timeSheet?.clockOut) return hours
            const shiftDate = new Date(timeSheet.date || timeSheet.clockIn)
            if (Number.isNaN(shiftDate.getTime()) || shiftDate < today || shiftDate >= tomorrow) return hours
            const elapsed = (new Date(timeSheet.clockOut) - new Date(timeSheet.clockIn)) / 3600000
            if (!Number.isFinite(elapsed) || elapsed <= 0) return hours
            const unpaidBreak = timeSheet.breakIncludedInPay === false ? (Number(timeSheet.breakMinutes) || 0) / 60 : 0
            return hours + Math.max(0, elapsed - unpaidBreak)
        }, 0)
        return total.toFixed(2)
    }, [timeSheets, today, tomorrow])
    const weeklyHours = useMemo(() => {
        const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 })
        const weekEnd = endOfWeek(new Date(), { weekStartsOn: 1 })
        const total = timeSheets.reduce((hours, timeSheet) => {
            if (!timeSheet?.clockIn || !timeSheet?.clockOut) return hours
            const shiftDate = new Date(timeSheet.date || timeSheet.clockIn)
            if (Number.isNaN(shiftDate.getTime()) || shiftDate < weekStart || shiftDate > weekEnd) return hours
            const elapsed = (new Date(timeSheet.clockOut) - new Date(timeSheet.clockIn)) / 3600000
            if (!Number.isFinite(elapsed) || elapsed <= 0) return hours
            const unpaidBreak = timeSheet.breakIncludedInPay === false ? (Number(timeSheet.breakMinutes) || 0) / 60 : 0
            return hours + Math.max(0, elapsed - unpaidBreak)
        }, 0)
        return {
            value: total.toFixed(2),
            period: `Mon ${format(weekStart, 'd MMM')} – Sun ${format(weekEnd, 'd MMM')}`,
        }
    }, [timeSheets])
    const displayedAllocationDay = useMemo(() => {
        const selectedDay = new Date(today)
        selectedDay.setHours(0, 0, 0, 0)
        return selectedDay
    }, [today])
    const displayedAllocationDayEnd = useMemo(
        () => addDays(displayedAllocationDay, 1),
        [displayedAllocationDay]
    )
    const displayedAllocations = useMemo(
        () => [
            ...allocations.map(allocation => ({ ...allocation, isIncomplete: false })),
            ...incompleteAllocations.map(allocation => ({ ...allocation, isIncomplete: true })),
        ].filter(allocation => {
            const allocationDate = allocation?.date ? new Date(allocation.date) : null
            return allocationDate && allocationDate >= displayedAllocationDay && allocationDate < displayedAllocationDayEnd
        }),
        [allocations, incompleteAllocations, displayedAllocationDay, displayedAllocationDayEnd]
    )
    const allocationCards = useMemo(
        () => displayedAllocations
            .map(allocation => ({
                ...allocation,
                client: clientNameById.get(allocation.chosenClient) || '',
                site: siteNameById.get(allocation.chosenSite) || '',
            }))
            .filter(allocation => {
                const search = normalizeSearch(allocationSearch.trim())
                if (!search) return true
                const workerNames = formatName((allocation.chosenWorkers || [])
                    .map(workerId => {
                        const worker = workerById.get(workerId)
                        return worker ? `${worker.firstName || ''} ${worker.lastName || ''}` : ''
                    })
                    .join(' '))
                return [allocation.client, allocation.site, workerNames]
                    .some(value => normalizeSearch(String(value || '')).includes(search))
            })
            .sort((first, second) => {
                const firstNeedsAttention = first.isIncomplete || (first.chosenWorkers || []).length !== Number(first.requiredWorkers || 0)
                const secondNeedsAttention = second.isIncomplete || (second.chosenWorkers || []).length !== Number(second.requiredWorkers || 0)
                if (firstNeedsAttention !== secondNeedsAttention) return firstNeedsAttention ? -1 : 1
                return new Date(first.date) - new Date(second.date)
            }),
        [displayedAllocations, allocationSearch, clientNameById, siteNameById, workerById]
    )
    // A worker shows in red until they accept (or complete) their shift.
    const acceptedShiftStatusMap = useMemo(
        () => new Map(
            timeSheets
                .filter(sheet => sheet?.allocationId && sheet?.userId)
                .map(sheet => {
                    const status = String(sheet.status || '').toLowerCase()
                    // Clocked in ('active') or approved also means the shift was accepted.
                    return [`${sheet.allocationId}-${sheet.userId}`, ['accepted', 'active', 'completed', 'approved'].includes(status)]
                })
        ),
        [timeSheets]
    )
    const completedShiftKeys = useMemo(
        () => new Set(
            timeSheets
                .filter(sheet => sheet?.allocationId && sheet?.userId)
                .filter(sheet => (sheet.clockIn && sheet.clockOut) || String(sheet.status || '').toLowerCase() === 'completed')
                .map(sheet => `${sheet.allocationId}-${sheet.userId}`)
        ),
        [timeSheets]
    )
    const incompleteTimesheetsCount = useMemo(() => {
        const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 })
        const weekEnd = endOfWeek(new Date(), { weekStartsOn: 1 })
        return timeSheets.filter(sheet => {
            const status = String(sheet?.status || '').toLowerCase()
            if (['completed', 'accepted', 'approved'].includes(status)) return false
            const entryDateValue = sheet?.date || sheet?.clockIn || sheet?.clockOut
            if (!entryDateValue) return false
            const entryDate = new Date(entryDateValue)
            return !Number.isNaN(entryDate.getTime()) && entryDate >= weekStart && entryDate <= weekEnd
        }).length
    }, [timeSheets])
    const workerAvailabilityRows = useMemo(() => {
        const activeLeaveByWorker = new Map()
        workerLeaves.forEach(leave => {
            const start = leave.startDate ? new Date(leave.startDate) : null
            const end = leave.endDate ? new Date(leave.endDate) : null
            // On leave today: started by the end of today and not yet finished.
            if (start && end && start < tomorrow && end >= today) {
                activeLeaveByWorker.set(leave.workerId, leave)
            }
        })

        const onsiteByWorker = new Map()
        todaysAllocations.forEach(allocation => {
            const siteName = siteNameById.get(allocation.chosenSite) || 'Allocated site'
            ;(allocation.chosenWorkers || []).forEach(workerId => {
                const currentSites = onsiteByWorker.get(workerId) || []
                onsiteByWorker.set(workerId, [...new Set([...currentSites, siteName])])
            })
        })

        const search = normalizeSearch(workerAvailabilitySearch.trim())
        return workers
            .map(worker => {
                const leave = activeLeaveByWorker.get(worker.id)
                const siteNames = onsiteByWorker.get(worker.id) || []
                const statusKey = leave ? 'leave' : siteNames.length ? 'site' : 'available'
                const workerName = formatName(`${worker.firstName || ''} ${worker.lastName || ''}`.trim() || 'Unknown')
                const statusLabel = statusKey === 'leave' ? 'On Leave' : statusKey === 'site' ? 'On Site' : 'Available'
                const detail = statusKey === 'leave'
                    ? formatLeavePeriod(leave.startDate, leave.endDate)
                    : statusKey === 'site'
                        ? `Site: ${siteNames.join(', ')}`
                        : ''
                return { ...worker, workerName, statusKey, statusLabel, detail }
            })
            .filter(worker => workerAvailabilityFilter === 'all' || worker.statusKey === workerAvailabilityFilter)
            .filter(worker => !search || [worker.workerName, worker.detail, worker.statusLabel, worker.workerType, worker.userType]
                .some(value => normalizeSearch(String(value || '')).includes(search)))
            .sort((first, second) => first.workerName.localeCompare(second.workerName))
    }, [workerLeaves, workers, todaysAllocations, siteNameById, today, tomorrow, workerAvailabilityFilter, workerAvailabilitySearch])

    const smsRecipientRows = useMemo(
        () => (showSendMessage?.chosenWorkers || []).map(workerId => {
            const worker = workerById.get(workerId)
            return {
                id: workerId,
                name: worker ? formatName(`${worker.firstName || ''} ${worker.lastName || ''}`).trim() || '-' : '-',
                contactNo: worker?.contactNo || '',
            }
        }),
        [showSendMessage, workerById]
    )
    const normalizedSmsExtraNumber = smsExtraNumber.replace(/\D/g, '')
    const selectedSmsWorkers = smsRecipientRows.filter(worker => !excludedSmsWorkerIds.includes(worker.id))
    const hasInvalidSmsExtraNumber = smsExtraNumber.trim().length > 0 && normalizedSmsExtraNumber.length !== 10
    const closeSmsModal = () => {
        setExcludedSmsWorkerIds([])
        setShowSendMessage(null)
        setSmsMessage('')
        setSmsExtraNumber('')
        setSmsAdditionalNumbers([])
    }
    const addSmsNumber = () => {
        if (normalizedSmsExtraNumber.length !== 10) {
            toast.error('Mobile number must be 10 digits.')
            return
        }
        if (smsAdditionalNumbers.includes(normalizedSmsExtraNumber)) {
            toast.error('This mobile number has already been added.')
            return
        }
        setSmsAdditionalNumbers(previous => [...previous, normalizedSmsExtraNumber])
        setSmsExtraNumber('')
    }
    const sendAllocationSms = async () => {
        if (!showSendMessage?.id || !activeOrg?.id || !smsMessage.trim()) return
        if (selectedSmsWorkers.length + smsAdditionalNumbers.length === 0) {
            toast.error('Choose at least one recipient.')
            return
        }
        if (smsExtraNumber.trim()) {
            toast.error('Click Add to include the additional mobile number.')
            return
        }
        try {
            setLoading(true)
            const hasExcludedWorkers = excludedSmsWorkerIds.length > 0
            const response = await postWithAuth(hasExcludedWorkers ? 'sendSmsToWorkerIds' : 'sendSmsToWorkers', {
                ...(hasExcludedWorkers
                    ? { allocationId: showSendMessage.id, workerIds: selectedSmsWorkers.map(worker => worker.id) }
                    : { id: showSendMessage.id }),
                message: smsMessage,
                orgId: activeOrg.id,
                extraNumbers: smsAdditionalNumbers,
            })
            if (response?.err) {
                toast.error(response.err)
                return
            }
            closeSmsModal()
            showConfirmation('Message sent to workers successfully.')
        } catch (error) {
            toast.error(error.message)
        } finally {
            setLoading(false)
        }
    }
    const editViewedAllocation = () => {
        const recordId = showViewAllocation?.id || showViewAllocation?.allocationId
        if (!recordId) {
            toast.error('Unable to open this allocation for editing.')
            return
        }
        const queryName = showViewAllocation.isIncomplete ? 'incompleteAllocationId' : 'allocationId'
        const allocationId = encodeURIComponent(recordId)
        const editRecord = showViewAllocation
        setShowViewAllocation(null)
        navigate(`/allocations?${queryName}=${allocationId}`, {
            state: { editAllocationRecord: editRecord, editIncomplete: !!editRecord.isIncomplete },
        })
    }

    const openAllocationDocument = async (document) => {
        setOpeningDocument(true)
        try {
            const resolvedUrl = await resolveStorageFileUrl(document, activeOrg?.id || '')
            if (!resolvedUrl) {
                throw new Error('File could not be opened.')
            }
            window.open(resolvedUrl, '_blank', 'noopener,noreferrer')
        } catch (e) {
            toast.error(e.message || 'Failed to open document')
        } finally {
            setOpeningDocument(false)
        }
    }
    const openAllocationEdit = (allocation) => {
        const recordId = allocation?.id || allocation?.allocationId
        if (!recordId) {
            toast.error('Unable to open this allocation for editing.')
            return
        }
        const queryName = allocation.isIncomplete ? 'incompleteAllocationId' : 'allocationId'
        navigate(`/allocations?${queryName}=${encodeURIComponent(recordId)}`, {
            state: { editAllocationRecord: allocation, editIncomplete: !!allocation.isIncomplete },
        })
    }

    return (<div>
        <Header />
        <LoadingPopup visible={loading || openingDocument} message={openingDocument ? 'Opening document...' : 'Loading...'} />
        <div className='pageShell'>
            <Sidebar />
            <PageContent as="div" className='pageContent'>
                <PageHeader title={"Overview"} subtitle={"Today's allocations, hours and items needing attention"} />
                <div className='pageBody'>
                <div className='allocationOverviewLayout dashboardOverviewLayout'>
                    <div className='dashboardMainColumn'>
                        <SectionCard className='cardSurface dashboardOverviewCard' title={<>Allocations</>}>

                            <div className='dashboardAllocationViewTopRow'>
                            <div className='allocationSummaryRow dashboardAllocationSummaryRow allocationSummaryFeatureBox'>
                                <div className='allocationSummaryBadge'><CalendarIcon style={{ width: 20, height: 20 }} /></div>
                                 <div className='allocationSummaryTitle'>
                                    Today
                                    <span className='allocationSummaryDate'>{format(displayedAllocationDay, 'EEE, d MMMM yyyy')}</span>
                                 </div>
                             </div>
                                <div className='allocationSearchRow dashboardAllocationSearchRowBottom'>
                                    <div className='allocationSearchFieldWithIcon'>

                                        <SearchInput
                                            disabled={false}
                                            value={allocationSearch}
                                            onChange={event => (setAllocationSearch)(event.target.value)}
                                            placeholder='Search'
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className='allocationCardGrid'>
                                {allocationCards.map(allocation => {
                                    const chosenWorkers = allocation.chosenWorkers || []
                                    const isCompleted = chosenWorkers.length > 0 && chosenWorkers.every(workerId => completedShiftKeys.has(`${allocation.id}-${workerId}`))
                                    const requiredWorkersCount = Number(allocation.requiredWorkers) || 0
                                    const needsAttention = !isCompleted && !!(
                                        allocation.isIncomplete
                                        || allocation.showAsIncomplete
                                        || allocation.assetRequired
                                        || (requiredWorkersCount > 0 && chosenWorkers.length !== requiredWorkersCount)
                                    )
                                    const isPending = !!allocation.pending && !isCompleted
                                    const showStatusBadge = !isCompleted && (isPending || needsAttention)
                                    const cardClassName = isCompleted
                                        ? 'allocationOverviewCard allocationOverviewCardCompleted'
                                        : isPending
                                            ? 'allocationOverviewCard allocationOverviewCardPending'
                                            : needsAttention
                                                ? 'allocationOverviewCard allocationOverviewCardNeedsAttention'
                                                : 'allocationOverviewCard'
                                    return (
                                    <div key={allocation.id} className={cardClassName}>
                                        <div className='allocationOverviewCardTopRow'>
                                            <div className='allocationOverviewCardField'><strong>Allocation ID:</strong> {allocation.allocationId || allocation.id}</div>
                                            {isCompleted && <span className='allocationOverviewCompleteBadge' aria-label='Completed' title='Completed'><CheckIcon /></span>}
                                        </div>
                                        <div className='allocationOverviewCardField allocationOverviewCardIconField'><CalendarIcon title='Date' />{format(new Date(allocation.date), 'EEE, d MMM yyyy')}</div>
                                        <div className='allocationOverviewCardField allocationOverviewCardIconField'><ClockIcon title='Time' />{formatTime(new Date(allocation.date))}</div>
                                        <div className='allocationOverviewCardField allocationOverviewCardIconField'><Building2Icon title='Client' />{allocation.client}</div>
                                        <div className='allocationOverviewCardField allocationOverviewCardIconField'><MapPinIcon title='Address' />{getAllocationCardLocationLabel(allocation) === null ? '—' : (formatAllocationCardAddress(getAllocationCardLocationLabel(allocation)) || '—')}</div>
                                        <div className='allocationOverviewCardField allocationOverviewCardIconField'><UsersIcon title='Required Workers' />{allocation.requiredWorkers || '-'}</div>
                                        <div className='allocationOverviewCardField allocationOverviewWorkersHeading'><strong>Workers:</strong></div>
                                        <div className='allocationOverviewWorkers'>
{!(allocation.chosenWorkers || []).some(Boolean) && <span className='noWorkersAssigned'>No workers assigned</span>}
                                            {Array.from({
                                                length: (allocation.chosenWorkers || []).length
                                            }, (_, workerIndex) => {
                                                const workerId = (allocation.chosenWorkers || [])[workerIndex]
                                                if (!workerId) return null
                                                const worker = workerById.get(workerId)
                                                const accepted = acceptedShiftStatusMap.get(`${allocation.id}-${workerId}`)
                                                return (
                                                    <div
                                                        key={workerId}
                                                        className={accepted ? 'allocationOverviewWorker' : 'allocationOverviewWorker allocationOverviewWorkerPending'}
                                                        title={accepted ? undefined : 'Shift not yet accepted'}
                                                    >
                                                        {workerIndex + 1}. {worker ? formatName(`${worker.firstName} ${worker.lastName}`) : workerId}
                                                    </div>
                                                )
                                            })}
                                            {!(Number(allocation.requiredWorkers) || (allocation.chosenWorkers || []).length) && <div className='allocationOverviewCardMuted'>—</div>}
                                        </div>
                                        {showStatusBadge && <span className={isPending ? 'dashboardIncompleteBadge dashboardPendingBadge' : 'dashboardIncompleteBadge'}>{isPending ? 'Pending' : 'Incomplete'}</span>}
                                        <div className='allocationOverviewFooter'>
                                            <Button data-action-tone="edit" variant="secondary" size="sm"  type='button' className='allocationOverviewTextButton allocationEditWhiteButton' onClick={() => openAllocationEdit(allocation)}>Edit</Button>
                                            <Button variant="secondary" size="sm" type='button' className='allocationOverviewPrimaryButton allocationOverviewViewButton' onClick={() => setShowViewAllocation(allocation)}>View</Button>
                                            <Button variant="secondary" size="sm" type='button' className='allocationOverviewPrimaryButton allocationOverviewSmsButton' disabled={!!allocation.isIncomplete} title={allocation.isIncomplete ? 'Complete this allocation before messaging its workers' : undefined} onClick={() => setShowSendMessage(allocation)}>Message</Button>
                                        </div>
                                    </div>
                                    )
                                })}
                                {!initialLoading && allocationCards.length === 0 && <div className='allocationEmptyState dashboardAllocationEmptyState'>No allocations scheduled for this period.</div>}
                            </div>
                        </SectionCard>
                    </div>
                    <div className='allocationOverviewAside dashboardAsideStack'>
                        <div className='dashboardKpiCompactGrid'>
                            <StatCard label={<>Today's Allocations</>} value={<>{scheduledTodayCount}</>} tone="blue" sublabel={<>Scheduled today</>} icon={<><MapPinIcon /></>} />
                            <StatCard label={<>Workers Deployed</>} value={<>{deployedWorkersTodayCount}</>} tone="blue" sublabel={<>Scheduled today</>} icon={<><UsersIcon /></>} />
                            <StatCard label={<>Today's Hours</>} value={<>{todaysHours}</>} tone="blue" sublabel={<>Recorded today</>} icon={<><ClockIcon /></>} />
                            <StatCard label={<>Weekly Hours</>} value={<>{weeklyHours.value}</>} tone="blue" sublabel={<>{weeklyHours.period}</>} icon={<><ClockIcon /></>} />
                        </div>
                        {[
                            { title: 'Incomplete Allocations', subtitle: 'Needs attention', value: incompleteAllocations.length, alert: true, Icon: AlertTriangleIcon, onClick: () => navigate('/allocations', { state: { activeOverviewSection: 'incomplete' } }) },
                            {
                                title: 'Incomplete Timesheets',
                                subtitle: 'Needs attention',
                                value: incompleteTimesheetsCount,
                                alert: true,
                                Icon: AlertCircleIcon,
                                onClick: () => navigate('/timesheet', {
                                    state: {
                                        timesheetStatus: 'Incomplete',
                                        weekStart: startOfWeek(new Date(), { weekStartsOn: 1 }).toISOString(),
                                    },
                                }),
                            },
                            { title: 'Incomplete Tasks', subtitle: 'Needs attention', value: taskSort.sortedData.length, alert: true, Icon: ListChecksIcon, onClick: () => navigate('/tasks') },
                            { title: 'Pending Worker Profile', subtitle: 'Needs attention', value: pendingWorkers.length, alert: true, Icon: UsersIcon, onClick: () => navigate('/workers?tab=pending') },
                            { title: 'Asset Registration', subtitle: 'Needs attention', value: expiredAssets.length, alert: true, Icon: CalendarIcon, onClick: () => navigate('/assets?filter=expired') },
                        ].map(({ title, subtitle, value, alert, Icon, onClick }) => (
                            <StatCard key={title} label={title} sublabel={subtitle} value={value} icon={<Icon />} tone={alert ? 'red' : 'blue'} onClick={onClick} />
                        ))}
                        <SectionCard className='cardSurface dashboardWorkerAvailabilityCard' title={<>Worker Status</>}>

                            <div className='workerAvailabilityBody'>
                                <div className="workerAvailabilitySearchField uiSearchContainer">

                                    <SearchInput
                                        type='text'
                                        value={workerAvailabilitySearch}
                                        onChange={event => setWorkerAvailabilitySearch(event.target.value)}
                                        placeholder='Search workers or capacity'
                                    />
                                </div>
                                <div className='workerAvailabilityFilters'>
                                    {[
                                        { key: 'leave', label: 'On Leave' },
                                        { key: 'site', label: 'On Site' },
                                        { key: 'available', label: 'Available' },
                                        { key: 'all', label: 'All' },
                                    ].map(filter => (
                                        <FilterChip active={workerAvailabilityFilter === filter.key}
                                            key={filter.key}
                                            type='button'
                                            className={workerAvailabilityFilter === filter.key ? 'workerAvailabilityFilter workerAvailabilityFilterActive' : 'workerAvailabilityFilter'}
                                            onClick={() => setWorkerAvailabilityFilter(filter.key)}
                                        >
                                            {filter.label}
                                        </FilterChip>
                                    ))}
                                </div>
                            </div>
                            <div className="workerAvailabilityTable">
                                {workerAvailabilityFilter === 'leave' && (
                                    <div className='workerAvailabilityTableHeader'>
                                        <span>Worker</span>
                                        <span>Period</span>
                                    </div>
                                )}
                                <ul className={['available', 'all'].includes(workerAvailabilityFilter) ? 'workerAvailabilityList workerAvailabilityListScrollable' : 'workerAvailabilityList'}>
                                    {workerAvailabilityRows.map(worker => (
                                        <li key={worker.id} className='workerAvailabilityItem'>
                                            <div className='workerAvailabilityInfo'>
                                                <div className='workerAvailabilityName'>
                                                    <span>{formatName(worker.workerName)}</span>
                                                </div>
                                                {worker.detail && workerAvailabilityFilter !== 'leave' && <div className='workerAvailabilitySubtext'>
                                                    {worker.statusKey === 'leave' && <CalendarIcon className='workerAvailabilityPeriodIcon' />}
                                                    <span className={worker.statusKey === 'leave' ? 'workerAvailabilityPeriodDates' : ''}>{worker.statusKey === 'leave' ? worker.detail.replace(' to ', ' to\n') : worker.detail}</span>
                                                </div>}
                                            </div>
                                            {workerAvailabilityFilter === 'leave'
                                                ? <div className='workerAvailabilityPeriod'>{worker.detail.replace(' to ', ' to\n')}</div>
                                                : <StatusBadge className='workerAvailabilityStatus'>{worker.statusLabel}</StatusBadge>}
                                        </li>
                                    ))}
                                    {workerAvailabilityRows.length === 0 && (
                                        <li className='workerAvailabilityItem'><div className='dashboardAllocationEmptyState'>No matching workers.</div></li>
                                    )}
                                </ul>
                            </div>
                        </SectionCard>
                    </div>
                </div>

                <div className='dashboardPanelGrid'>

                    {appointmentSort.sortedData.length > 0 && (
                        <Section title={'Appointments (Today & Next Day)'} className='width-100 recordPanelCard' variant='card'>
                            <div className='recordPanelTableWrap'>
                            <Table emptyItems="appointments" className='recordPanelTable'>
                                <TableHead>
                                    <TableRow>
                                        <TableHeaderCell {...appointmentSort.getSortableHeaderProps('dueDate')}>
                                            Date & Time {appointmentSort.getSortIcon('dueDate')}
                                        </TableHeaderCell>
                                        <TableHeaderCell {...appointmentSort.getSortableHeaderProps('client')}>
                                            Client {appointmentSort.getSortIcon('client')}
                                        </TableHeaderCell>
                                        <TableHeaderCell {...appointmentSort.getSortableHeaderProps('site')}>
                                            Site {appointmentSort.getSortIcon('site')}
                                        </TableHeaderCell>
                                        <TableHeaderCell>Comments</TableHeaderCell>
                                    </TableRow>
                                </TableHead>
                                <tbody>
                                    {appointmentSort.sortedData.map(appointment => (
                                        <TableRow key={appointment.id}>
                                            <TableCell>{format(new Date(appointment.dueDate), 'EEE, d MMM yyyy')}<br />{formatTime(new Date(appointment.dueDate))}</TableCell>
                                            <TableCell>{appointment.client}</TableCell>
                                            <TableCell>{appointment.site}</TableCell>
                                            <TableCell>{appointment.comments ? appointment.comments.substring(0, 10) + '...' : ''}</TableCell>
                                        </TableRow>
                                    ))}
                                </tbody>
                            </Table>
                            </div>
                        </Section>
                    )}

                </div>

                <ModalComponent
                    showModal={!!showViewAllocation}
                    setShowModal={setShowViewAllocation}
                    customMaxWidth='1100px'
                    customContentStyle={{ maxHeight: '94dvh', overflow: 'hidden', padding: 0, backgroundColor: '#f8fafc', borderRadius: '16px', width: 'calc(100vw - 32px)', minWidth: 0 }}
                >
                    {showViewAllocation && (() => {
                        const assignedWorkersCount = (showViewAllocation.chosenWorkers || []).length
                        const requiredWorkersCount = Number(showViewAllocation.requiredWorkers) || 0
                        const isIncomplete = showViewAllocation.isIncomplete || showViewAllocation.showAsIncomplete || showViewAllocation.assetRequired || (requiredWorkersCount > 0 && assignedWorkersCount !== requiredWorkersCount)
                        const teamLeaderIds = (showViewAllocation.teamLeaders || []).map(item => item?.value || item)
                        const assignmentMap = new Map((showViewAllocation.chosenWorkerAssignments || []).map(item => [item.workerId, item.capacity]))
                        const selectedSite = siteById.get(showViewAllocation.chosenSite)
                        const siteSupervisorName = `${selectedSite?.managerFirstName || ''} ${selectedSite?.managerLastName || ''}`.trim() || '-'
                        const siteSupervisorContact = selectedSite?.managerContactNo || '-'
                        const siteSupervisorPhoneLink = siteSupervisorContact !== '-' ? `tel:${String(siteSupervisorContact).replace(/[^\d+]/g, '')}` : ''
                        const hasMapMarker = !!showViewAllocation.dropMapMarker
                            || !!String(showViewAllocation.mapMarkerAddress || showViewAllocation.mapMarkerName || '').trim()
                            || (Array.isArray(showViewAllocation.mapMarkerCoordinates) && showViewAllocation.mapMarkerCoordinates.length >= 2)
                        const locationLabel = hasMapMarker
                            ? (showViewAllocation.mapMarkerAddress || showViewAllocation.mapMarkerName || getAllocationCardLocationLabel(showViewAllocation))
                            : (selectedSite?.address || '')
                        const documents = Array.isArray(showViewAllocation.document)
                            ? showViewAllocation.document
                            : showViewAllocation.document ? [showViewAllocation.document] : []

                        return <div className='allocationDetailModal'>
                            <div className='allocationDetailHeader'>
                                <div className='allocationDetailHeadingBlock'>
                                    <h2 className='allocationDetailTitle'>Allocation Details</h2>
                                    <div className='allocationDetailId'>Allocation ID: {showViewAllocation.allocationId || showViewAllocation.id || '-'}</div>
                                </div>
                                <span className={`allocationDetailStatus ${isIncomplete ? 'allocationDetailStatusIncomplete' : 'allocationDetailStatusComplete'}`}>
                                    {isIncomplete ? 'Incomplete' : 'Complete'}
                                </span>
                            </div>
                            <div className='allocationDetailBody'>
                                <SectionCard className='allocationDetailCard' title={<>Job</>}>

                                    <div className='allocationDetailJobGrid'>
                                        <div className='allocationDetailItem'><span className='allocationDetailLabel'>Allocation ID</span>{showViewAllocation.allocationId || showViewAllocation.id || '-'}</div>
                                        <div className='allocationDetailItem'><span className='allocationDetailLabel'>Date/Time</span>{showViewAllocation.date ? <>{format(new Date(showViewAllocation.date), 'EEE, d MMM yyyy')}<br />{formatTime(new Date(showViewAllocation.date))}</> : '-'}</div>
                                        <div className='allocationDetailItem'><span className='allocationDetailLabel'>Client Name</span>{showViewAllocation.client || '-'}</div>
                                        <div className='allocationDetailItem'><span className='allocationDetailLabel'>Site Name</span>{showViewAllocation.site || '-'}</div>
                                        <div className='allocationDetailItem allocationDetailJobSiteAddress'><span className='allocationDetailLabel'>Location Address</span>{locationLabel || '-'}</div>
                                        <div className='allocationDetailItem allocationDetailJobMapMarker'><span className='allocationDetailLabel'>Map Marker</span>{hasMapMarker ? (showViewAllocation.mapMarkerName || 'Yes') : 'No'}</div>
                                        <div className='allocationDetailItem allocationDetailItemWide'>
                                            <span className='allocationDetailLabel'>Site Supervisor</span>
                                            <span className='allocationDetailSupervisorLine'>
                                                <span>{siteSupervisorName}</span>
                                                {siteSupervisorPhoneLink ? <a href={siteSupervisorPhoneLink} className='allocationDetailLink'>{siteSupervisorContact}</a> : <span>-</span>}
                                            </span>
                                        </div>
                                        <div className='allocationDetailItem allocationDetailItemWide'><span className='allocationDetailLabel'>Required Workers</span>{requiredWorkersCount || '-'}</div>
                                    </div>
                                </SectionCard>
                                <SectionCard className='allocationDetailCard allocationDetailWorkersAssetsCard' title={<>Workers &amp; Assets</>}>

                                    <div className='allocationDetailWorkersAssetsGrid'>
                                        <div className='allocationDetailWorkersColumn'>
                                            <h4 className='allocationDetailSubsectionTitle'>Worker</h4>
                                            {assignedWorkersCount > 0 ? <div className='allocationDetailWorkerStack'>
                                                {(showViewAllocation.chosenWorkers || []).map(workerId => {
                                                    const worker = workerById.get(workerId)
                                                    return <div key={workerId} className='allocationDetailWorkerPanel'>
                                                        <div className='allocationDetailWorkerPanelName'>{worker ? formatName(`${formatName(worker.firstName) || ''} ${formatName(worker.lastName) || ''}`).trim() : workerId}{teamLeaderIds.includes(workerId) ? ' (TL)' : ''}</div>
                                                        {worker?.contactNo && <a className='allocationDetailWorkerMobile' href={`tel:${worker.contactNo}`}>{formatPhone(worker.contactNo)}</a>}
                                                                {assignmentMap.get(workerId) && <div className='allocationDetailWorkerPanelSubtext'>{assignmentMap.get(workerId)}</div>}
                                                    </div>
                                                })}
                                            </div> : <p className='allocationDetailEmptyText'>No workers allocated</p>}
                                            <div className='allocationDetailSectionMeta allocationDetailWorkerCount'>Workers: {assignedWorkersCount}/{requiredWorkersCount || assignedWorkersCount} Allocated</div>
                                        </div>
                                        <div className='allocationDetailAssetsColumn'>
                                            <h4 className='allocationDetailSubsectionTitle'>Assets</h4>
                                            {(showViewAllocation.chosenAssets || []).length > 0 ? <div className='allocationDetailAssetStack'>
                                                {showViewAllocation.chosenAssets.map((chosenAsset, index) => {
                                                    const assetId = chosenAsset?.asset || chosenAsset?.value || chosenAsset
                                                    const asset = assetById.get(assetId)
                                                    const assignedWorker = workerById.get(chosenAsset?.worker)
                                                    return <div key={`${assetId}-${index}`} className='allocationDetailAssetPanel'>
                                                        <div className='allocationDetailAssetPanelTitle'>{assignedWorker ? formatName(`${formatName(assignedWorker.firstName) || ''} ${formatName(assignedWorker.lastName) || ''}`).trim() : 'No worker assigned'}</div>
                                                        <div className='allocationDetailAssetPanelText'>{asset?.machineId || asset?.assetId || assetId || 'Asset'}{asset?.regoNo ? ` - Reg: ${asset.regoNo}` : ''}</div>
                                                    </div>
                                                })}
                                            </div> : <p className='allocationDetailEmptyText'>No assets assigned</p>}
                                            <div className='allocationDetailFlagRow'>
                                                <div className='allocationDetailFlagItem'><span className='allocationDetailLabel'>Signs Dropped</span>{showViewAllocation.signsDroppedToSite ? 'Yes' : 'No'}</div>
                                                <div className='allocationDetailFlagItem'><span className='allocationDetailLabel'>Signs Onsite Already</span>{showViewAllocation.signsOnsiteAlready ? 'Yes' : 'No'}</div>
                                                <div className='allocationDetailFlagItem'><span className='allocationDetailLabel'>Asset Required</span>{showViewAllocation.assetRequired ? 'Yes' : 'No'}</div>
                                            </div>
                                        </div>
                                    </div>
                                </SectionCard>
                                <SectionCard className='allocationDetailCard' title={<>Details</>}>

                                    <div className='allocationDetailNotesGrid'>
                                        <div className='allocationDetailItem allocationDetailItemWide'><span className='allocationDetailLabel'>Scope of Works</span><div className='allocationPreWrapCell'>{showViewAllocation.scopeOfWorks || '-'}</div></div>
                                        <div className='allocationDetailItem'><span className='allocationDetailLabel'>Comments</span><div className='allocationPreWrapCell'>{showViewAllocation.comments || '-'}</div></div>
                                        <div className='allocationDetailItem'><span className='allocationDetailLabel'>Admin Notes</span><div className='allocationPreWrapCell'>{showViewAllocation.adminNotes || '-'}</div></div>
                                        <div className='allocationDetailItem'>
                                            <div className='allocationDetailDocumentHeaders'><span className='allocationDetailLabel'>Documents</span><span className='allocationDetailLabel'>Action</span></div>
                                            {documents.length > 0 ? <div className='allocationDetailDocumentList'>
                                                {documents.map((document, index) => {
                                                    const url = document?.url || document?.filePath || document?.siteDocumentUrl || ''
                                                    const label = document?.name || `Document ${index + 1}`
                                                    return <div key={`${label}-${index}`} className='allocationDetailDocumentRow'>
                                                        {url ? <Button variant="secondary" size="md" type='button' className='allocationDetailLink allocationDetailDocumentViewButton' onClick={() => openAllocationDocument(document)}>{label}</Button> : <span>{label}</span>}
                                                        {url && <Button variant="secondary" size="md" type='button' className='allocationDetailLink allocationDetailDocumentViewButton' onClick={() => openAllocationDocument(document)}>Open</Button>}
                                                    </div>
                                                })}
                                            </div> : <div className='allocationInfoEmptyText'>No documents attached</div>}
                                        </div>
                                    </div>
                                </SectionCard>
                                <SectionCard className='allocationDetailCard' title={<>Activity Log</>}>

                                    <div className='allocationActivityGrid'>
                                        <div><h4 className='allocationActivityHeading'>Allocation Activity</h4><p className='allocationInfoEmptyText'>Open the Allocations page to view activity history.</p></div>
                                        <div><h4 className='allocationActivityHeading'>Message History</h4><p className='allocationInfoEmptyText'>Open the Allocations page to view message history.</p></div>
                                    </div>
                                </SectionCard>
                            </div>
                            <div className='allocationDetailFooter'>
                                <Button data-action-tone="cancel" variant="secondary" size="md"  type='button' className='secondaryButton allocationDetailFooterButton' onClick={() => setShowViewAllocation(null)}>Close</Button>
                                <Button data-action-tone="edit" variant="secondary" size="md"  type='button' className='allocationDetailFooterButton allocationDetailFooterButtonPrimary' onClick={editViewedAllocation}>Edit</Button>
                            </div>
                        </div>
                    })()}
                </ModalComponent>
                <LoadingPopup visible={loading || openingDocument} message={openingDocument ? 'Opening document...' : 'Loading...'} />

                <ModalComponent
                    showModal={!!showSendMessage}
                    setShowModal={closeSmsModal}
                    customMaxWidth='460px'
                    customContentStyle={{ width: 'calc(100% - 32px)', minWidth: 0, padding: 0, overflow: 'hidden' }}
                >
                    <div className='sendSmsModal'>
                        <div className='sendSmsHeader'>
                            <div>
                                <h2 className='sendSmsTitle'>Send SMS</h2>
                                <div className='sendSmsAllocation sendSmsAllocationDetails'>
                                    <div>Allocation ID: {showSendMessage?.allocationId || showSendMessage?.id || '-'}</div>
                                    <div>Client: {showSendMessage?.client || '-'}</div>
                                </div>
                            </div>
                        </div>
                        <div className='sendSmsBody'>
                            <div className='sendSmsRecipients'>
                                {smsRecipientRows.map(worker => (
                                    <Button variant="secondary" size="md"
                                        key={worker.id}
                                        type='button'
                                        className='sendSmsRecipientChip sendSmsRecipientToggle'
                                        aria-pressed={!excludedSmsWorkerIds.includes(worker.id)}
                                        disabled={loading}
                                        onClick={() => setExcludedSmsWorkerIds(previous => previous.includes(worker.id)
                                            ? previous.filter(id => id !== worker.id)
                                            : [...previous, worker.id])}
                                    >
                                        <span aria-hidden='true'>{excludedSmsWorkerIds.includes(worker.id) ? '+' : '✓'}</span>
                                        {formatName(worker.name)} · {formatPhone(worker.contactNo || 'No number on file')}
                                    </Button>
                                ))}
                                {smsAdditionalNumbers.map(number => (
                                    <span key={number} className='sendSmsRecipientChip sendSmsAdditionalChip'>
                                        {number}
                                        <Button variant="secondary" size="icon" type='button' aria-label={`Remove ${number}`} onClick={() => setSmsAdditionalNumbers(previous => previous.filter(item => item !== number))}>×</Button>
                                    </span>
                                ))}
                            </div>
                            <FieldLabel className='sendSmsFieldLabel' htmlFor='dashboard-allocation-sms-message'>Message</FieldLabel>
                            <Textarea
                                id='dashboard-allocation-sms-message'
                                className='sendSmsMessageInput'
                                disabled={loading}
                                value={smsMessage}
                                onChange={event => setSmsMessage(event.target.value)}
                                placeholder='Type your message...'
                            />
                            <FieldLabel className='sendSmsFieldLabel' htmlFor='dashboard-allocation-sms-contact'>Add Contact</FieldLabel>
                            <div className='sendSmsAddContactRow'>
                                <div>
                                    <UiInput
                                        id='dashboard-allocation-sms-contact'
                                        inputMode='tel'
                                        disabled={loading}
                                        value={smsExtraNumber}
                                        onChange={event => setSmsExtraNumber(event.target.value)}
                                        onKeyDown={event => {
                                            if (event.key === 'Enter') {
                                                event.preventDefault()
                                                addSmsNumber()
                                            }
                                        }}
                                        placeholder='Add additional mobile number'
                                    />
                                    {hasInvalidSmsExtraNumber && <p className='sendSmsContactError'>Mobile number must be 10 digits.</p>}
                                </div>
                                <Button variant="create" size="md" type='button' className='secondaryButton sendSmsAddButton' disabled={loading || hasInvalidSmsExtraNumber || !smsExtraNumber.trim()} onClick={addSmsNumber}>
                                    <span aria-hidden='true'>+</span> Add
                                </Button>
                            </div>
                        </div>
                        <div className='sendSmsFooter'>
                            <Button data-action-tone="cancel" variant="secondary" size="md"  className='secondaryButton sendSmsFooterButton' onClick={closeSmsModal}>Cancel</Button>
                            <Button variant="secondary" size="md" className='sendSmsFooterButton' disabled={loading || !smsMessage.trim() || smsExtraNumber.trim().length > 0 || selectedSmsWorkers.length + smsAdditionalNumbers.length === 0} onClick={sendAllocationSms}>
                                Send ({selectedSmsWorkers.length + smsAdditionalNumbers.length})
                            </Button>
                        </div>
                    </div>
                </ModalComponent>
                </div>
            </PageContent>
        </div>
    </div>)
}

export default Dashboard
